import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Confetti from "react-confetti";
import Footer from "../layouts/Footer";

// ============================================
// STYLES PARTAGÉS (utilisés dans tous les cas)
// ============================================
const AC_STYLES = `
  /* ========== GLOBAL ========== */
  .ac-page {
    background: #f8fafc;
    min-height: 100vh;
  }
  .ac-page * {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  /* ========== LOADING ========== */
  .ac-loading {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
  }
  .ac-loading-spinner {
    width: 56px;
    height: 56px;
    border: 5px solid #e2e8f0;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: ac-spin 0.8s linear infinite;
    margin-bottom: 20px;
  }
  .ac-loading-text {
    color: #64748b;
    font-size: 0.9rem;
    font-weight: 500;
  }
  @keyframes ac-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  /* ========== EMPTY STATE ========== */
  .ac-empty-state {
    background: white;
    border-radius: 20px;
    border: 1px solid #e2e8f0;
    padding: 60px 30px;
    text-align: center;
    max-width: 500px;
    margin: 60px auto;
    box-shadow: 0 4px 12px -4px rgba(0,0,0,0.05);
  }
  .ac-empty-icon {
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: linear-gradient(135deg, #fef3c7, #fde68a);
    color: #d97706;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 40px;
    margin-bottom: 24px;
  }
  .ac-empty-title {
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 12px;
    font-size: 1.3rem;
  }
  .ac-empty-text {
    color: #64748b;
    margin-bottom: 24px;
    font-size: 0.9rem;
  }

  /* ========== CARD ========== */
  .ac-card {
    background: white;
    border-radius: 18px;
    overflow: hidden;
    box-shadow: 0 20px 40px -12px rgba(0,0,0,0.1);
    border: 1px solid #e2e8f0;
    transition: all 0.4s ease;
  }
  .ac-card-pending {
    animation: ac-float 3s ease-in-out infinite;
  }
  .ac-card-success {
    box-shadow: 0 20px 40px -12px rgba(16, 185, 129, 0.3), 0 0 40px rgba(16, 185, 129, 0.15);
  }
  .ac-card-failed {
    animation: ac-shake 0.6s ease-in-out;
  }
  @keyframes ac-float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
  }
  @keyframes ac-shake {
    0%, 100% { transform: translateX(0); }
    20% { transform: translateX(-8px); }
    40% { transform: translateX(8px); }
    60% { transform: translateX(-8px); }
    80% { transform: translateX(8px); }
  }

  /* ========== HEADER ========== */
  .ac-card-header {
    position: relative;
    padding: 22px 22px 18px;
    text-align: center;
    color: white;
    overflow: hidden;
  }
  .ac-card-header-pending {
    background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  }
  .ac-card-header-success {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  }
  .ac-card-header-failed {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  }
  .ac-card-header-icon {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: rgba(255,255,255,0.2);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    margin-bottom: 10px;
    backdrop-filter: blur(10px);
    animation: ac-pulse-icon 2s ease-in-out infinite;
  }
  .ac-card-header-success .ac-card-header-icon,
  .ac-card-header-failed .ac-card-header-icon {
    animation: none;
  }
  @keyframes ac-pulse-icon {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.08); opacity: 0.9; }
  }
  .ac-card-header-title {
    font-weight: 700;
    font-size: 1.1rem;
    margin: 0 0 3px;
    letter-spacing: -0.02em;
  }
  .ac-card-header-subtitle {
    color: rgba(255,255,255,0.85);
    font-size: 0.75rem;
    margin: 0;
  }
  .ac-pulse-ring {
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    border: 3px solid rgba(255,255,255,0.3);
    animation: ac-pulse-ring 2s ease-out infinite;
  }
  @keyframes ac-pulse-ring {
    0% { transform: scale(0.95); opacity: 0.6; }
    100% { transform: scale(1.05); opacity: 0; }
  }

  /* ========== BODY ========== */
  .ac-card-body {
    padding: 18px;
  }

  /* ========== SUCCESS HERO ========== */
  .ac-success-hero {
    text-align: center;
    padding: 16px;
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
    border-radius: 12px;
    border: 1px solid #bbf7d0;
    margin-bottom: 16px;
  }
  .ac-success-hero-label {
    font-size: 0.68rem;
    color: #065f46;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 6px;
  }
  .ac-success-hero-value {
    font-size: 1.7rem;
    font-weight: 700;
    color: #059669;
    letter-spacing: -0.03em;
    line-height: 1;
  }
  .ac-success-hero-credits {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-top: 8px;
    padding: 4px 10px;
    background: white;
    border-radius: 20px;
    font-size: 0.72rem;
    font-weight: 700;
    color: #065f46;
    border: 1px solid #bbf7d0;
  }
  .ac-success-hero-credits > i {
    color: #f59e0b;
  }

  /* ========== DETAILS ========== */
  .ac-details {
    background: #f8fafc;
    border-radius: 12px;
    padding: 12px;
    border: 1px solid #e2e8f0;
    margin-bottom: 14px;
  }
  .ac-details-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 700;
    color: #0f172a;
    font-size: 0.75rem;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e2e8f0;
  }
  .ac-details-title > i {
    color: #3b82f6;
    font-size: 0.9rem;
  }
  .ac-details-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  .ac-detail-item {
    background: white;
    padding: 8px 10px;
    border-radius: 8px;
    border: 1px solid #f1f5f9;
  }
  .ac-detail-item-full {
    grid-column: 1 / -1;
  }
  .ac-detail-label {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.62rem;
    color: #94a3b8;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 3px;
  }
  .ac-detail-label > i {
    color: #cbd5e1;
    font-size: 0.75rem;
  }
  .ac-detail-value {
    font-weight: 700;
    color: #0f172a;
    font-size: 0.8rem;
    word-break: break-word;
  }
  .ac-detail-value-mono {
    font-family: 'SF Mono', Menlo, Monaco, monospace;
    font-size: 0.72rem;
    color: #1e40af;
  }

  /* ========== COUNTDOWN ========== */
  .ac-countdown {
    margin-bottom: 10px;
  }
  .ac-countdown-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 5px;
  }
  .ac-countdown-label {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.72rem;
    color: #64748b;
    font-weight: 600;
  }
  .ac-countdown-label > i {
    color: #3b82f6;
    font-size: 0.8rem;
  }
  .ac-countdown-value {
    font-size: 0.85rem;
    font-weight: 700;
    color: #3b82f6;
    font-variant-numeric: tabular-nums;
  }
  .ac-countdown-progress {
    height: 6px;
    background: #f1f5f9;
    border-radius: 10px;
    overflow: hidden;
  }
  .ac-countdown-progress-bar {
    height: 100%;
    background: linear-gradient(90deg, #3b82f6, #8b5cf6);
    border-radius: 10px;
    transition: width 0.8s linear;
  }

  /* ========== INFO BOX ========== */
  .ac-info-box {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    margin-bottom: 8px;
  }
  .ac-info-box-blue {
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    color: #1e40af;
    border-left: 3px solid #3b82f6;
  }
  .ac-info-box-icon {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #3b82f6;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    flex-shrink: 0;
  }
  .ac-info-box-content strong {
    display: block;
    font-size: 0.78rem;
    margin-bottom: 1px;
  }
  .ac-info-box-content p {
    margin: 0;
    font-size: 0.72rem;
    opacity: 0.85;
    line-height: 1.4;
  }

  /* ========== POLLING ========== */
  .ac-polling-status {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #f8fafc;
    border-radius: 8px;
    font-size: 0.72rem;
    color: #64748b;
    font-weight: 500;
  }
  .ac-polling-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #3b82f6;
    animation: ac-polling 1.5s ease-in-out infinite;
    flex-shrink: 0;
  }
  @keyframes ac-polling {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(0.8); }
  }

  /* ========== ERROR BOX ========== */
  .ac-error-box {
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
    border-radius: 12px;
    padding: 12px 14px;
    border-left: 3px solid #ef4444;
  }
  .ac-error-box-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.82rem;
    color: #991b1b;
    margin-bottom: 8px;
  }
  .ac-error-box-header > i {
    color: #ef4444;
    font-size: 1rem;
  }
  .ac-error-box-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  .ac-error-box-list li {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 3px 0;
    font-size: 0.75rem;
    color: #991b1b;
  }
  .ac-error-box-list li > i {
    font-size: 1rem;
    color: #ef4444;
  }

  /* ========== FOOTER ========== */
  .ac-card-footer {
    padding: 12px 18px 16px;
    display: flex;
    gap: 8px;
    justify-content: stretch;
  }
  .ac-card-footer > button {
    flex: 1;
  }

  /* ========== BUTTONS ========== */
  .ac-btn-primary, .ac-btn-secondary, .ac-btn-success, .ac-btn-outline-success {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 9px 14px;
    border-radius: 10px;
    font-weight: 600;
    font-size: 0.78rem;
    border: none;
    transition: all 0.2s;
    cursor: pointer;
    font-family: inherit;
  }
  .ac-btn-primary {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
  }
  .ac-btn-primary:hover {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    transform: translateY(-2px);
    box-shadow: 0 12px 24px -6px rgba(59, 130, 246, 0.5);
  }
  .ac-btn-secondary {
    background: white;
    border: 1px solid #e2e8f0;
    color: #334155;
  }
  .ac-btn-secondary:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    transform: translateY(-2px);
  }
  .ac-btn-success {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
  }
  .ac-btn-success:hover {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    transform: translateY(-2px);
    box-shadow: 0 12px 24px -6px rgba(16, 185, 129, 0.5);
  }
  .ac-btn-outline-success {
    background: white;
    border: 2px solid #10b981;
    color: #059669;
  }
  .ac-btn-outline-success:hover {
    background: #10b981;
    color: white;
    transform: translateY(-2px);
  }

  /* ========== RESPONSIVE ========== */
  @media (max-width: 768px) {
    .ac-card-header {
      padding: 18px 16px 14px;
    }
    .ac-card-header-title {
      font-size: 1rem;
    }
    .ac-card-header-icon {
      width: 46px;
      height: 46px;
      font-size: 22px;
    }
    .ac-card-body {
      padding: 14px;
    }
    .ac-details-grid {
      grid-template-columns: 1fr;
    }
    .ac-detail-item-full {
      grid-column: 1;
    }
    .ac-card-footer {
      flex-direction: column;
      padding: 10px 14px 14px;
    }
    .ac-success-hero-value {
      font-size: 1.4rem;
    }
  }
`;

// ============================================
// COMPOSANT PRINCIPAL
// ============================================
export default function AttenteConfirmationPaiement() {
  const navigate = useNavigate();
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [countdown, setCountdown] = useState(40);
  const [pollingCount, setPollingCount] = useState(0);
  const [transactionId, setTransactionId] = useState(null);

  // ============================================
  // RÉCUPÉRATION DES DONNÉES
  // ============================================
  useEffect(() => {
    const storedPayment = localStorage.getItem("pendingPayment");
    if (storedPayment) {
      try {
        const parsedData = JSON.parse(storedPayment);
        setPaymentData(parsedData);
        setTransactionId(parsedData.transactionId);
      } catch (err) {
        console.error("Erreur parsing paymentData:", err);
      }
    }
    setLoading(false);
  }, []);

  // ============================================
  // POLLING STATUT
  // ============================================
  useEffect(() => {
    if (!transactionId) return;

    let intervalId, timeoutId, countdownInterval;
    let stopped = false;

    const stopAll = () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
      clearInterval(countdownInterval);
      stopped = true;
    };

    const checkPaymentStatus = async () => {
      if (stopped || status === "failed" || status === "success") return;

      try {
        const token = localStorage.getItem("authToken");
        const response = await fetch(`/check-payment-status/${transactionId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();
        setPollingCount((prev) => prev + 1);

        const apiStatus = data.payment?.status;

        if (apiStatus === "SUCCESS") {
          setStatus("success");
          localStorage.removeItem("pendingPayment");
          stopAll();
        } else if (apiStatus === "FAILED") {
          setStatus("failed");
          localStorage.removeItem("pendingPayment");
          stopAll();
        }
      } catch (err) {
        console.error("Erreur polling:", err);
      }
    };

    checkPaymentStatus();
    intervalId = setInterval(checkPaymentStatus, 5000);

    timeoutId = setTimeout(() => {
      if (!stopped && status === "pending") {
        setStatus("failed");
        localStorage.removeItem("pendingPayment");
        stopAll();
      }
    }, 40000);

    countdownInterval = setInterval(() => {
      setCountdown((prev) => (prev > 1 ? prev - 1 : 0));
    }, 1000);

    return () => stopAll();
  }, [transactionId, status]);

  const formatMontant = (montant) =>
    new Intl.NumberFormat("fr-FR").format(montant) + " FCFA";

  // ============================================
  // CHARGEMENT
  // ============================================
  if (loading) {
    return (
      <div className="ac-page main main-app">
        <style>{AC_STYLES}</style>
        <div className="ac-loading">
          <div className="ac-loading-spinner"></div>
          <div className="ac-loading-text">Chargement...</div>
        </div>
      </div>
    );
  }

  // ============================================
  // PAS DE DONNÉES
  // ============================================
  if (!paymentData) {
    return (
      <div className="ac-page main main-app p-3 p-lg-4">
        <style>{AC_STYLES}</style>
        <div className="ac-empty-state">
          <div className="ac-empty-icon">
            <i className="bi bi-shield-exclamation"></i>
          </div>
          <h4 className="ac-empty-title">Données de paiement introuvables</h4>
          <p className="ac-empty-text">
            Aucune transaction en cours n'a été trouvée.
          </p>
          <button
            className="ac-btn-primary"
            onClick={() => navigate("/apps/recharge")}
          >
            <i className="bi bi-arrow-left"></i>
            Retour à la recharge
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  // ============================================
  // RENDER PRINCIPAL
  // ============================================
  return (
    <div className="ac-page main main-app p-3 p-lg-4">
      <style>{AC_STYLES}</style>

      {status === "success" && (
        <Confetti
          numberOfPieces={200}
          recycle={false}
          style={{ zIndex: 9999 }}
        />
      )}

      <Row className="justify-content-center g-4">
        <Col lg={7} md={10} xl={6}>
          <motion.div
            key={status}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className={`ac-card ac-card-${status}`}>
              {/* ========== HEADER ========== */}
              <div className={`ac-card-header ac-card-header-${status}`}>
                <div className="ac-card-header-icon">
                  {status === "pending" && <i className="bi bi-hourglass-split"></i>}
                  {status === "success" && <i className="bi bi-check-circle-fill"></i>}
                  {status === "failed" && <i className="bi bi-x-octagon-fill"></i>}
                </div>
                <h3 className="ac-card-header-title">
                  {status === "pending" && "Confirmation en cours..."}
                  {status === "success" && "Paiement confirmé 🎉"}
                  {status === "failed" && "Échec du paiement"}
                </h3>
                <p className="ac-card-header-subtitle">
                  {status === "pending" && "Vérification de votre transaction"}
                  {status === "success" && "Votre compte a été rechargé"}
                  {status === "failed" && "La transaction n'a pas abouti"}
                </p>
                {status === "pending" && <div className="ac-pulse-ring"></div>}
              </div>

              {/* ========== BODY ========== */}
              <div className="ac-card-body">
                {status === "success" && (
                  <div className="ac-success-hero">
                    <div className="ac-success-hero-label">Montant crédité</div>
                    <div className="ac-success-hero-value">
                      {formatMontant(paymentData.amount)}
                    </div>
                    {paymentData.creditsGranted && (
                      <div className="ac-success-hero-credits">
                        <i className="bi bi-star-fill"></i>
                        {paymentData.creditsGranted.toLocaleString()} crédits ajoutés
                      </div>
                    )}
                  </div>
                )}

                <div className="ac-details">
                  <div className="ac-details-title">
                    <i className="bi bi-receipt"></i>
                    Détails de la transaction
                  </div>
                  <div className="ac-details-grid">
                    <div className="ac-detail-item">
                      <div className="ac-detail-label">
                        <i className="bi bi-cash-stack"></i>Montant
                      </div>
                      <div className="ac-detail-value">
                        {formatMontant(paymentData.amount)}
                      </div>
                    </div>
                    <div className="ac-detail-item">
                      <div className="ac-detail-label">
                        <i className="bi bi-credit-card"></i>Méthode
                      </div>
                      <div className="ac-detail-value">
                        {paymentData.paymentMethod}
                      </div>
                    </div>
                    <div className="ac-detail-item ac-detail-item-full">
                      <div className="ac-detail-label">
                        <i className="bi bi-hash"></i>Référence
                      </div>
                      <div className="ac-detail-value ac-detail-value-mono">
                        {paymentData.transactionId}
                      </div>
                    </div>
                    {paymentData.phoneNumber && (
                      <div className="ac-detail-item">
                        <div className="ac-detail-label">
                          <i className="bi bi-telephone"></i>Numéro
                        </div>
                        <div className="ac-detail-value ac-detail-value-mono">
                          {paymentData.phoneNumber}
                        </div>
                      </div>
                    )}
                    {paymentData.country && (
                      <div className="ac-detail-item">
                        <div className="ac-detail-label">
                          <i className="bi bi-flag"></i>Pays
                        </div>
                        <div className="ac-detail-value">{paymentData.country}</div>
                      </div>
                    )}
                  </div>
                </div>

                {status === "pending" && (
                  <>
                    <div className="ac-countdown">
                      <div className="ac-countdown-header">
                        <span className="ac-countdown-label">
                          <i className="bi bi-clock-history"></i>Temps restant
                        </span>
                        <span className="ac-countdown-value">{countdown}s</span>
                      </div>
                      <div className="ac-countdown-progress">
                        <div
                          className="ac-countdown-progress-bar"
                          style={{ width: `${(countdown / 40) * 100}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="ac-info-box ac-info-box-blue">
                      <div className="ac-info-box-icon">
                        <i className="bi bi-phone"></i>
                      </div>
                      <div className="ac-info-box-content">
                        <strong>Action requise</strong>
                        <p>Confirmez la transaction sur votre téléphone mobile.</p>
                      </div>
                    </div>

                    {pollingCount > 0 && (
                      <div className="ac-polling-status">
                        <div className="ac-polling-dot"></div>
                        Vérification n°{pollingCount} en cours...
                      </div>
                    )}
                  </>
                )}

                {status === "failed" && (
                  <div className="ac-error-box">
                    <div className="ac-error-box-header">
                      <i className="bi bi-exclamation-triangle-fill"></i>
                      <strong>Causes possibles</strong>
                    </div>
                    <ul className="ac-error-box-list">
                      <li>
                        <i className="bi bi-dot"></i>Solde insuffisant
                      </li>
                      <li>
                        <i className="bi bi-dot"></i>Code PIN incorrect
                      </li>
                      <li>
                        <i className="bi bi-dot"></i>Problème de connexion réseau
                      </li>
                      <li>
                        <i className="bi bi-dot"></i>Délai de confirmation dépassé
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {/* ========== FOOTER ========== */}
              <div className="ac-card-footer">
                {status === "pending" && (
                  <button
                    className="ac-btn-secondary"
                    onClick={() => navigate("/apps/recharge")}
                  >
                    <i className="bi bi-x-circle"></i>Annuler
                  </button>
                )}

                {status === "success" && (
                  <>
                    <button
                      className="ac-btn-outline-success"
                      onClick={() => navigate("/apps/HistoriquePaiement")}
                    >
                      <i className="bi bi-clock-history"></i>Voir l'historique
                    </button>
                    <button
                      className="ac-btn-success"
                      onClick={() => navigate("/apps/recharge")}
                    >
                      <i className="bi bi-plus-circle"></i>Nouvelle recharge
                    </button>
                  </>
                )}

                {status === "failed" && (
                  <>
                    <button
                      className="ac-btn-secondary"
                      onClick={() => navigate("/apps/HistoriquePaiement")}
                    >
                      <i className="bi bi-clock-history"></i>Historique
                    </button>
                    <button
                      className="ac-btn-primary"
                      onClick={() => navigate("/apps/recharge")}
                    >
                      <i className="bi bi-arrow-clockwise"></i>Réessayer
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </Col>
      </Row>

      <Footer />
    </div>
  );
}